import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../../../services/api";
import Loader from "../../../core/common/loader";
import { showBasicAlert } from "../../../core/common/useAlert";
import { all_routes } from "../../router/all_routes";
import FieldMappingFlow from "./FieldMappingFlow";
import { ReactFlowProvider } from "reactflow";
import CommonSelect from "../../../core/common/commonSelect";
import Datatable from "../../../core/common/dataTable";
import { Dayjs } from "dayjs";
import { DatePicker } from "antd";

const { RangePicker } = DatePicker;

interface FacebookPage {
  access_token: string;
  category: string;
  category_list: any[];
  name: string;
  id: string;
  tasks: string[];
  page_id?: string;
  page_name?: string;
}

interface FacebookForm {
  form_id: string;
  name: string;
  form_name?: string;
  created_time: string;
  status: string;
  total_leads: number;
  leads: any[];
  page_id?: string;
  page_name?: string;
}

interface Lead {
  lead_id: string;
  created_time: string;
  ad_id?: string;
  ad_name?: string;
  adset_id?: string;
  adset_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  form_id: string;
  is_organic?: boolean;
  form_name?: string;
  page_id?: string;
  page_name?: string;
  [key: string]: any;
}

interface FacebookCredential {
  id?: number;
  name: string;
  user_access_token: string;
  app_scoped_user_id: string;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CRMFormField {
  id: number | string;
  type: string;
  label: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface CRMForm {
  key: number;
  LeadFormName: string;
  CompanyName: string;
  CompanyId: number;
  CompanyType: string;
  structure: CRMFormField[];
  Pipeline: string;
  employees: number[];
  pipeline_id: number;
  deal_pipeline_id: number | null;
}

interface FieldMapping {
  id?: number;
  form_id: string;
  crm_form_id: number;
  source_field: string;
  source_field_label?: string;
  target_field_id: string;
  target_field_label: string;
  mapping_type: "automatic" | "manual" | "custom";
  is_active: boolean;
  mapping_order?: number;
  transformation_rule?: string;
  created_at?: string;
}

const FacebookLeadsConfig: React.FC = () => {
  const location = useLocation();
  const signature = localStorage.getItem("signature");
  const routes = all_routes;

  const [isLoading, setIsLoading] = useState(false);
  const [isSavingCredential, setIsSavingCredential] = useState(false);

  // Company state
  const [availableCompanies, setAvailableCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companyDetails, setCompanyDetails] = useState<any>(null);

  // Tabs state
  const [activeTab, setActiveTab] = useState<"credentials" | "pages" | "forms">(
    "credentials"
  );

  // Credentials state
  const [newCredential, setNewCredential] = useState({
    name: "",
    user_access_token: "",
    app_scoped_user_id: "",
  });

  // Saved credentials state
  const [savedCredentials, setSavedCredentials] = useState<
    FacebookCredential[]
  >([]);
  const [selectedCredential, setSelectedCredential] =
    useState<FacebookCredential | null>(null);

  // Pages state
  const [userPages, setUserPages] = useState<FacebookPage[]>([]);

  // Forms state
  const [selectedPage, setSelectedPage] = useState<FacebookPage | null>(null);
  const [pageForms, setPageForms] = useState<FacebookForm[]>([]);
  const [syncingPages, setSyncingPages] = useState<Set<string>>(new Set());

  // Manual fetch state
  const [manualFetchData, setManualFetchData] = useState({
    page_id: "",
    page_access_token: "",
  });

  // Leads modal state
  const [selectedForm, setSelectedForm] = useState<FacebookForm | null>(null);
  const [showLeadsModal, setShowLeadsModal] = useState(false);

  // CRM Forms state
  const [crmForms, setCrmForms] = useState<CRMForm[]>([]);
  const [selectedCrmForm, setSelectedCrmForm] = useState<CRMForm | null>(null);
  const [showReactFlowModal, setShowReactFlowModal] = useState(false);
  const [selectedFormForMapping, setSelectedFormForMapping] =
    useState<FacebookForm | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [deleteRecord, setDeleteRecord] = useState<{
    type: "credential" | "mapping" | "allMappings";
    id?: number;
    data?: any;
  } | null>(null);
  const [syncConfirmation, setSyncConfirmation] = useState<{
    form: FacebookForm | null;
    fieldMappings: FieldMapping[] | null;
    isAlreadySynced: boolean;
  } | null>(null);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [refreshStatus, setRefreshStatus] = useState(false);
  // Forms mapping state
  const [formsWithMappings, setFormsWithMappings] = useState<Set<string>>(
    new Set()
  );
  const [formsSyncing, setFormsSyncing] = useState<Set<string>>(new Set());
  const [formsSynced, setFormsSynced] = useState<Set<string>>(new Set());
  const [formsDestination, setFormsDestination] = useState<Map<string, string>>(new Map());
  const [mappingState, setMappingState] = useState<{
    loading: boolean;
    hasMappings: boolean;
    crmFormId: number | null;
    isSyncing: boolean;
  }>({
    loading: false,
    hasMappings: false,
    crmFormId: null,
    isSyncing: false,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedPageForSync, setSelectedPageForSync] = useState<FacebookPage | null>(null)

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
    if (dates === null) {
      setDateRange([null, null]);
    } else {
      setDateRange(dates);
    }
  };
  useEffect(() => {
    fetchAvailableCompanies();
  }, []);

  // Fetch available companies on component mount
  useEffect(() => {
    if (companyDetails) {
      fetchSavedCredentials();
      fetchCRMForms();
    }
  }, [companyDetails]);

  const fetchAvailableCompanies = async () => {
    try {
      setIsLoading(true);
      const response = await api.post("userService/getAllCompanyList", {
        signature,
      });

      if (response.data?.data?.data) {
        const companies = response.data.data.data;
        setAvailableCompanies(companies);

        // Auto-select the first company if available
        if (companies.length > 0) {
          handleCompanySelect(companies[0]);
        } else {
          // No companies available
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      showBasicAlert("Error loading company list");
      setIsLoading(false);
    }
  };

  const handleCompanySelect = (company: any) => {
    setSelectedCompany(company);
    setCompanyDetails({
      companyId: company.id,
      companyType: company.type,
      companyName: company.name,
    });

    // Clear previous data when company changes
    setSavedCredentials([]);
    setUserPages([]);
    setPageForms([]);
    setCrmForms([]);
    setSelectedCrmForm(null);
  };

  const fetchCRMForms = async (): Promise<boolean> => {
    if (!companyDetails) return false;

    try {
      const response = await api.post("leadService/getLeadFormsByCompany", {
        signature,
        company_id: companyDetails.companyId,
        company_type: companyDetails.companyType,
      });

      if (response.data?.msg === "Data Found") {
        const forms = response.data?.data?.data || [];
        setCrmForms(forms);

        if (forms.length > 0) {
          if (!selectedCrmForm) {
            setSelectedCrmForm(forms[0]);
          }
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("Error fetching CRM forms:", error);
      showBasicAlert("Error loading CRM forms");
      return false;
    }
  };

  const fetchSavedCredentials = async () => {
    try {
      setIsLoading(true);
      const response = await api.post("leadService/getAllFacebookCredentials", {
        signature,
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
      });

      if (response.data.message?.[0]?.status) {
        setSavedCredentials(response.data.message[0].data || []);
      }
    } catch (error) {
      console.error("Error fetching credentials:", error);
      showBasicAlert("Error fetching saved credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const checkFormMappingsExistAPI = async (
    facebookFormIds: string | string[]
  ) => {
    try {
      const response = await api.post("leadService/checkFormMappingsExist", {
        signature,
        facebook_form_ids: Array.isArray(facebookFormIds)
          ? facebookFormIds
          : [facebookFormIds],
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
      });

      return response.data;
    } catch (error) {
      console.error("Error checking form mappings:", error);
      throw error;
    }
  };

  const getFieldMappingsSyncStatusAPI = async (facebookFormId: string) => {
    try {
      const response = await api.post(
        "leadService/getFieldMappingsSyncStatus",
        {
          signature,
          facebook_form_id: facebookFormId,
          company_id: companyDetails?.companyId,
          company_type: companyDetails?.companyType,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching sync status:", error);
      throw error;
    }
  };

  const saveFieldMappingsAPI = async (mappingsData: any) => {
    try {
      const processedMappings = mappingsData.mappings.map((mapping: any) => ({
        ...mapping,
        transformation_rule: mapping.transformation_rule || "",
      }));
      const response = await api.post("leadService/saveFieldMappings", {
        signature,
        form_id: mappingsData.form_id,
        crm_form_id: mappingsData.crm_form_id,
        facebook_form_name: mappingsData.facebook_form_name,
        crm_form_name: mappingsData.crm_form_name,
        facebook_page_id: mappingsData.facebook_page_id,
        fb_app_scoped_user_id: mappingsData.fb_app_scoped_user_id,
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
        mappings: processedMappings,
        facebook_page_name: mappingsData.facebook_page_name,
      });
      return response.data;
    } catch (error) {
      console.error("Error saving field mappings:", error);
      throw error;
    }
  };

  const getFieldMappingsByFormAPI = async (
    facebookFormId: string,
    crmFormId: number
  ) => {
    try {
      const response = await api.post("leadService/getFieldMappingsByForm", {
        signature,
        facebook_form_id: facebookFormId,
        crm_form_id: crmFormId,
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching field mappings:", error);
      throw error;
    }
  };

  const updateFieldMappingAPI = async (mappingId: number, updateData: any) => {
    try {
      const response = await api.post("leadService/updateFieldMapping", {
        signature,
        id: mappingId,
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
        ...updateData,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating field mapping:", error);
      throw error;
    }
  };

  const syncToLeadBankAPI = async (
    facebookFormId: string,
    facebookFormName: string,
    facebookPageId: string,
    facebookPageName: string,
    fbAppScopedUserId: string
  ) => {
    try {
      const response = await api.post("leadService/syncToLeadBank", {
        signature,
        facebook_form_id: facebookFormId,
        facebook_form_name: facebookFormName,
        facebook_page_id: facebookPageId,
        facebook_page_name: facebookPageName,
        fb_app_scoped_user_id: fbAppScopedUserId,
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
      });
      return response.data;
    } catch (error) {
      console.error("Error setting up Lead Bank sync:", error);
      throw error;
    }
  };

  const deleteFieldMappingAPI = async (mappingId: number) => {
    try {
      const response = await api.post("leadService/deleteFieldMapping", {
        signature,
        id: mappingId,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting field mapping:", error);
      throw error;
    }
  };

  const deleteMappingsByFormAPI = async (
    facebookFormId: string,
    crmFormId: number
  ) => {
    try {
      const response = await api.post("leadService/deleteMappingsByForm", {
        signature,
        facebook_form_id: facebookFormId,
        crm_form_id: crmFormId,
        company_id: companyDetails?.companyId,
        company_type: companyDetails?.companyType,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting mappings by form:", error);
      throw error;
    }
  };

  useEffect(() => {
    const loadMappingsStatus = async () => {
      if (pageForms.length === 0 || !selectedCrmForm) return;

      try {
        // Collect all form IDs
        const formIds = pageForms.map(form => form.form_id);

        // Make a single API call for all forms
        const response = await checkFormMappingsExistAPI(formIds);

        if (response.message && Array.isArray(response.message)) {
          const mappedForms = new Set<string>();
          const syncingForms = new Set<string>();
          const syncedForms = new Set<string>();
          const destinations = new Map<string, string>();

          // Process all results
          response.message.forEach((item: any) => {
            if (item.status && item.data) {
              const formId = item.data.facebook_form_id;

              if (item.data.has_mappings) {
                mappedForms.add(formId);
              }
              if (item.data.is_syncing) {
                syncingForms.add(formId);
              }
              if (item.data.is_synced) {
                syncedForms.add(formId);
              }
              if (item.data.is_lead_bank !== undefined) {
                destinations.set(formId, item.data.is_lead_bank ? "LEAD_BANK" : "QUEUE");
              }

              // Update mappingState for the currently selected form in the modal
              if (selectedFormForMapping?.form_id === formId) {
                setMappingState({
                  loading: false,
                  hasMappings: Boolean(item.data.has_mappings),
                  crmFormId: item.data.crm_form_id ?? null,
                  isSyncing: Boolean(item.data.is_syncing),
                });
              }
            }
          });

          setFormsWithMappings(mappedForms);
          setFormsSyncing(syncingForms);
          setFormsSynced(syncedForms);
          setFormsDestination(destinations);
        }
      } catch (error) {
        console.error("Error loading mappings status:", error);
      }
    };

    loadMappingsStatus();
  }, [pageForms, selectedCrmForm, refreshStatus]);

  const updateFieldMapping = async (
    mappingId: number,
    updateData: Partial<FieldMapping>
  ) => {
    try {
      setIsLoading(true);
      const response = await updateFieldMappingAPI(mappingId, updateData);
      if (response.message?.[0]?.status) {
        setFieldMappings((prev) =>
          prev.map((mapping) =>
            mapping.id === mappingId ? { ...mapping, ...updateData } : mapping
          )
        );
      } else {
        showBasicAlert(
          response.message?.[0]?.msg || "Failed to update mapping"
        );
      }
    } catch (error: any) {
      console.error("Error updating field mapping:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg ||
        "Error updating field mapping";
      showBasicAlert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRecord) return;

    try {
      setIsLoading(true);

      switch (deleteRecord.type) {
        case "credential":
          if (deleteRecord.id) {
            const response = await api.post(
              "leadService/deleteFacebookCredential",
              {
                signature,
                id: deleteRecord.id,
              }
            );

            if (response.data.message?.[0]?.status) {
              showBasicAlert("Credential deleted successfully!");
              await fetchSavedCredentials();
              if (selectedCredential?.id === deleteRecord.id) {
                setSelectedCredential(null);
                setNewCredential({
                  name: "",
                  user_access_token: "",
                  app_scoped_user_id: "",
                });
              }
            } else {
              showBasicAlert("Failed to delete credential");
            }
          }
          break;

        case "mapping":
          if (deleteRecord.id) {
            const response = await deleteFieldMappingAPI(deleteRecord.id);
            if (response.message?.[0]?.status) {
              setFieldMappings((prev) =>
                prev.filter((mapping) => mapping.id !== deleteRecord.id)
              );
              showBasicAlert("Field mapping deleted successfully!");
            } else {
              showBasicAlert(
                response.message?.[0]?.msg || "Failed to delete mapping"
              );
            }
          }
          break;

        case "allMappings":
          if (deleteRecord.data) {
            const response = await deleteMappingsByFormAPI(
              deleteRecord.data.facebookFormId,
              deleteRecord.data.crmFormId
            );

            if (response.message?.[0]?.status) {
              setFieldMappings([]);
              showBasicAlert("All field mappings deleted successfully!");
              if (selectedFormForMapping?.form_id === deleteRecord.data.facebookFormId) {
                setMappingState({
                  loading: false,
                  hasMappings: false,
                  crmFormId: null,
                  isSyncing: false,
                });

                setFormsWithMappings(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(deleteRecord.data.facebookFormId);
                  return newSet;
                });

                setFormsSynced(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(deleteRecord.data.facebookFormId);
                  return newSet;
                });
              }
              if (pageForms.length > 0) {
                const formIds = pageForms.map(form => form.form_id);
                try {
                  const refreshResponse = await checkFormMappingsExistAPI(formIds);
                  if (refreshResponse.message && Array.isArray(refreshResponse.message)) {
                    const mappedForms = new Set<string>();
                    const syncedForms = new Set<string>();
                    const destinations = new Map<string, string>();

                    refreshResponse.message.forEach((item: any) => {
                      if (item.status && item.data) {
                        const formId = item.data.facebook_form_id;
                        if (item.data.has_mappings) {
                          mappedForms.add(formId);
                        }
                        if (item.data.is_synced) {
                          syncedForms.add(formId);
                        }
                        if (item.data.is_lead_bank !== undefined) {
                          destinations.set(formId, item.data.is_lead_bank ? "LEAD_BANK" : "QUEUE");
                        }
                      }
                    });

                    setFormsWithMappings(mappedForms);
                    setFormsSynced(syncedForms);
                    setFormsDestination(prev => new Map([...Array.from(prev), ...Array.from(destinations)]));
                  }
                } catch (error) {
                  console.error("Error refreshing mappings status:", error);
                }
              }
            } else {
              showBasicAlert(
                response.message?.[0]?.msg || "Failed to delete mappings"
              );
            }
          }
          break;
      }
    } catch (error: any) {
      console.error("Error deleting:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg || "Error deleting";
      showBasicAlert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getDeleteModalTitle = () => {
    if (!deleteRecord) return "Delete Item";

    switch (deleteRecord.type) {
      case "credential":
        return "Delete Credential";
      case "mapping":
        return "Delete Field Mapping";
      case "allMappings":
        return "Delete All Mappings";
      default:
        return "Delete Item";
    }
  };

  const getDeleteModalMessage = () => {
    if (!deleteRecord) return "Are you sure you want to delete this item?";

    switch (deleteRecord.type) {
      case "credential":
        return "Are you sure you want to delete this Facebook credential? This action cannot be undone.";
      case "mapping":
        return "Are you sure you want to delete this field mapping? This action cannot be undone.";
      case "allMappings":
        return "Are you sure you want to delete ALL field mappings for this form? This action cannot be undone and will remove all your mapping configurations.";
      default:
        return "Are you sure you want to delete this item? This action cannot be undone.";
    }
  };

  const CustomDeleteModal = () => {
    if (!deleteRecord) return null;

    return (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="avatar avatar-xl bg-transparent-danger text-danger mb-3">
                <i className="ti ti-trash-x fs-36" />
              </span>
              <h4 className="mb-1">{getDeleteModalTitle()}</h4>
              <p className="mb-3">{getDeleteModalMessage()}</p>
              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-light me-3"
                  onClick={() => setDeleteRecord(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={async () => {
                    await handleDelete();
                    setDeleteRecord(null);
                  }}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const openMappingModal = async (form: FacebookForm) => {
    setSelectedFormForMapping(form);
    await fetchCRMForms();

    try {
      const response = await checkFormMappingsExistAPI(form.form_id);

      if (response.message?.[0]?.data) {
        const data = response.message[0].data;

        setMappingState({
          loading: false,
          hasMappings: Boolean(data?.has_mappings),
          crmFormId: data?.crm_form_id ?? null,
          isSyncing: Boolean(data?.is_syncing),
        });

        if (data.crm_form_id) {
          const mappedCrmForm = crmForms.find(f => f.key === data.crm_form_id);
          if (mappedCrmForm) {
            setSelectedCrmForm(mappedCrmForm);
          } else {
            if (crmForms.length > 0) {
              setSelectedCrmForm(crmForms[0]);
            }
          }
        } else {
          if (crmForms.length > 0) {
            setSelectedCrmForm(crmForms[0]);
          }
        }

        if (data.crm_form_id) {
          await loadExistingMappings(form.form_id, data.crm_form_id);
        }
      }
    } catch (error) {
      console.error("Error checking form mapping:", error);
      if (crmForms.length > 0) {
        setSelectedCrmForm(crmForms[0]);
      }
    }

    setShowReactFlowModal(true);
  };

  useEffect(() => {
    if (selectedFormForMapping && showReactFlowModal) {
      const targetCrmFormId = mappingState.crmFormId || selectedCrmForm?.key;

      if (targetCrmFormId) {
        loadExistingMappings(selectedFormForMapping.form_id, targetCrmFormId);
      }
    }
  }, [selectedCrmForm, showReactFlowModal, mappingState.crmFormId]);

  const loadExistingMappings = async (
    facebookFormId: string,
    crmFormId: number
  ) => {
    try {

      setIsLoading(true);
      const response = await getFieldMappingsByFormAPI(
        facebookFormId,
        crmFormId
      );

      if (response.message?.[0]?.status && response.message[0].data) {
        const existingMappings = response.message[0].data;
        setFieldMappings(existingMappings);
      }
    } catch (error) {
      console.error("Error loading existing mappings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const closeMappingModal = () => {
    setShowReactFlowModal(false);
    setSelectedFormForMapping(null);
    setFieldMappings([]);
  };

  const getAvailableFacebookFields = (): string[] => {
    if (
      !selectedFormForMapping?.leads ||
      selectedFormForMapping.leads.length === 0
    ) {
      return [];
    }

    const fields = new Set<string>();
    selectedFormForMapping.leads.forEach((lead: Lead) => {
      Object.keys(lead).forEach((key) => {
        if (
          ![
            "lead_id",
            "created_time",
            "ad_id",
            "ad_name",
            "adset_id",
            "adset_name",
            "campaign_id",
            "campaign_name",
            "form_id",
            "form_name",
            "is_organic",
            "page_id",
            "page_name",
          ].includes(key)
        ) {
          fields.add(key);
        }
      });
    });
    return Array.from(fields).sort();
  };

  const autoMapToCRMFields = () => {
    if (!selectedCrmForm || !selectedFormForMapping) return;

    const facebookFields = getAvailableFacebookFields();
    const newMappings: FieldMapping[] = [];

    facebookFields.forEach((facebookField) => {
      const suggestedCrmField = suggestCrmField(
        facebookField,
        selectedCrmForm.structure
      );
      if (suggestedCrmField) {
        const existingMapping = fieldMappings.find(
          (m) => m.source_field === facebookField
        );
        if (!existingMapping) {
          newMappings.push({
            source_field: facebookField,
            target_field_id: suggestedCrmField.id.toString(),
            target_field_label: suggestedCrmField.label,
            crm_form_id: selectedCrmForm.key,
            form_id: selectedFormForMapping.form_id,
            mapping_type: "automatic",
            is_active: true,
            source_field_label: facebookField,
          });
        }
      }
    });

    if (newMappings.length > 0) {
      setFieldMappings((prev) => [...prev, ...newMappings]);
      showBasicAlert(
        `Automatically mapped ${newMappings.length} fields to CRM form!`
      );
    } else {
      showBasicAlert("No new automatic mappings found.");
    }
  };

  const suggestCrmField = (
    facebookField: string,
    crmFields: CRMFormField[]
  ): CRMFormField | null => {
    const field = facebookField.toLowerCase();

    const fieldMatchers = [
      {
        facebook: ["name", "full_name", "first_name", "last_name"],
        crm: ["name", "full name", "fullname"],
      },
      { facebook: ["email", "email_address"], crm: ["email", "e-mail"] },
      {
        facebook: ["phone", "phone_number", "mobile"],
        crm: ["phone", "mobile", "telephone", "contact"],
      },
      { facebook: ["city"], crm: ["city", "location"] },
      { facebook: ["company"], crm: ["company", "organization"] },
    ];

    for (const matcher of fieldMatchers) {
      if (matcher.facebook.some((f) => field.includes(f))) {
        const matchedField = crmFields.find((crmField) =>
          matcher.crm.some((crmPattern) =>
            crmField.label.toLowerCase().includes(crmPattern)
          )
        );
        if (matchedField) return matchedField;
      }
    }

    return null;
  };

  const saveAllMappings = async () => {
    try {
      if (!selectedFormForMapping || !selectedCrmForm || !companyDetails) {
        showBasicAlert("Please select company, Facebook form and CRM form");
        return;
      }

      setIsLoading(true);

      const currentCredential = selectedCredential || {
        app_scoped_user_id: newCredential.app_scoped_user_id,
      };

      const facebookPageId =
        selectedFormForMapping.page_id ||
        selectedPage?.id ||
        manualFetchData.page_id;

      const facebookPageName =
        selectedFormForMapping.page_name || selectedPage?.name || "";

      const mappingsData = {
        form_id: selectedFormForMapping.form_id,
        crm_form_id: selectedCrmForm.key,
        facebook_form_name: selectedFormForMapping.name,
        crm_form_name: selectedCrmForm.LeadFormName,
        facebook_page_id: facebookPageId,
        facebook_page_name: facebookPageName,
        fb_app_scoped_user_id: currentCredential.app_scoped_user_id,
        company_id: companyDetails.companyId,
        company_type: companyDetails.companyType,
        mappings: fieldMappings.map((mapping) => ({
          source_field: mapping.source_field,
          target_field_id: mapping.target_field_id,
          source_field_label:
            mapping.source_field_label || mapping.source_field,
          target_field_label: mapping.target_field_label,
          mapping_type: mapping.mapping_type,
          is_active: mapping.is_active !== undefined ? mapping.is_active : true,
          mapping_order: mapping.mapping_order || 0,
          transformation_rule: mapping.transformation_rule || null,
        })),
      };

      const response = await saveFieldMappingsAPI(mappingsData);

      if (response.message?.[0]?.status) {
        showBasicAlert(
          `Successfully saved ${fieldMappings.length} field mappings!`
        );

        if (selectedFormForMapping) {
          setFormsWithMappings((prev) =>
            new Set(prev).add(selectedFormForMapping.form_id)
          );
        }
        closeMappingModal();
      } else {
        showBasicAlert(response.message?.[0]?.msg || "Failed to save mappings");
      }
    } catch (error: any) {
      console.error("Error saving mappings:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg ||
        "Error saving field mappings";
      showBasicAlert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const saveCredential = async () => {
    if (
      !newCredential.name ||
      !newCredential.user_access_token ||
      !newCredential.app_scoped_user_id
    ) {
      showBasicAlert("Please fill all credential fields");
      return;
    }

    try {
      setIsSavingCredential(true);

      if (selectedCredential?.id) {
        const response = await api.post(
          "leadService/updateFacebookCredential",
          {
            signature,
            id: selectedCredential.id,
            name: newCredential.name.trim(),
            user_access_token: newCredential.user_access_token,
            app_scoped_user_id: newCredential.app_scoped_user_id,
            company_id: companyDetails?.companyId,
            company_type: companyDetails?.companyType,
          }
        );

        if (response.data.message?.[0]?.status) {
          showBasicAlert("Credential updated successfully!");
          setNewCredential({
            name: "",
            user_access_token: "",
            app_scoped_user_id: "",
          });
          setSelectedCredential(null);
          await fetchSavedCredentials();
        } else {
          showBasicAlert(
            response.data.message?.[0]?.msg || "Failed to update credential"
          );
        }
      } else {
        const response = await api.post(
          "leadService/createFacebookCredential",
          {
            signature,
            name: newCredential.name.trim(),
            user_access_token: newCredential.user_access_token,
            app_scoped_user_id: newCredential.app_scoped_user_id,
            company_id: companyDetails?.companyId,
            company_type: companyDetails?.companyType,
          }
        );

        if (response.data.message?.[0]?.status) {
          showBasicAlert("Credential saved successfully!");
          setNewCredential({
            name: "",
            user_access_token: "",
            app_scoped_user_id: "",
          });
          await fetchSavedCredentials();
        } else {
          showBasicAlert(
            response.data.message?.[0]?.msg || "Failed to save credential"
          );
        }
      }
    } catch (error: any) {
      console.error("Error saving credential:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg || "Error saving credential";
      showBasicAlert(errorMessage);
    } finally {
      setIsSavingCredential(false);
    }
  };

  const loadCredential = (credential: FacebookCredential) => {
    setSelectedCredential(credential);
    setNewCredential({
      name: credential.name,
      user_access_token: credential.user_access_token,
      app_scoped_user_id: credential.app_scoped_user_id,
    });
  };

  const createNewCredential = () => {
    setSelectedCredential(null);
    setNewCredential({
      name: "",
      user_access_token: "",
      app_scoped_user_id: "",
    });
  };

  const handleUseCredentialAndFetchPages = async (
    credential: FacebookCredential
  ) => {
    setSelectedCredential(credential);
    setNewCredential({
      name: credential.name,
      user_access_token: credential.user_access_token,
      app_scoped_user_id: credential.app_scoped_user_id,
    });

    await fetchUserPagesWithCredential(credential);
  };

  const fetchUserPagesWithCredential = async (
    credential?: FacebookCredential
  ) => {
    const accessToken =
      credential?.user_access_token || newCredential.user_access_token;
    const userId =
      credential?.app_scoped_user_id || newCredential.app_scoped_user_id;

    if (!accessToken || !userId) {
      showBasicAlert("Please add credentials first");
      return;
    }

    try {
      setIsLoading(true);

      const response = await api.post("leadService/fetchUserPages", {
        signature,
        user_access_token: accessToken,
        app_scoped_user_id: userId,
      });

      if (response.data.message?.[0]?.status) {
        setUserPages(response.data.message[0].data.pages || []);
        setActiveTab("pages");
      } else {
        showBasicAlert("Failed to fetch pages");
      }
    } catch (error) {
      console.error("Error fetching user pages:", error);
      showBasicAlert("Error fetching Facebook pages");
    } finally {
      setIsLoading(false);
    }
  };

  const syncLeadForms = (page: FacebookPage) => {
    setSelectedPageForSync(page);
    setShowDatePicker(true);
  };

  const handleFetchWithDate = async () => {
    if (!selectedPageForSync) return;

    try {
      setSyncingPages((prev) => new Set(prev).add(selectedPageForSync.id));
      setSelectedPage(selectedPageForSync);
      setManualFetchData({
        page_id: selectedPageForSync.id,
        page_access_token: selectedPageForSync.access_token,
      });

      // Prepare API data
      const apiData: any = {
        signature,
        page_id: selectedPageForSync.id,
        page_access_token: selectedPageForSync.access_token,
        app_scoped_user_id: newCredential.app_scoped_user_id,
      };

      // Add date range if selected
      if (dateRange[0] && dateRange[1]) {
        apiData.start_date = dateRange[0].format('YYYY-MM-DD');
        apiData.end_date = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.post("leadService/fetchFacebookLeads", apiData);

      if (response.data.message?.[0]?.status) {
        setPageForms(response.data.message[0].data.forms || []);
        setActiveTab("forms");

        if (dateRange[0] && dateRange[1]) {
          showBasicAlert(`Fetched leads from ${dateRange[0].format('MMM DD, YYYY')} to ${dateRange[1].format('MMM DD, YYYY')}`);
        } else {
          showBasicAlert("Fetched all leads");
        }
      } else {
        showBasicAlert("Failed to fetch lead forms");
      }
    } catch (error) {
      console.error("Error syncing lead forms:", error);
      showBasicAlert("Error syncing lead forms");
    } finally {
      setSyncingPages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedPageForSync.id);
        return newSet;
      });
      setShowDatePicker(false);
      setSelectedPageForSync(null);
    }
  };

  const openLeadsModal = (form: FacebookForm) => {
    setSelectedForm(form);
    setShowLeadsModal(true);
  };

  const closeLeadsModal = () => {
    setSelectedForm(null);
    setShowLeadsModal(false);
  };

  const processFacebookLeadsEnhanced = async (
    form: FacebookForm,
    crm_form_id: number,
    fieldMappings: FieldMapping[]
  ) => {
    try {
      if (!form.leads || form.leads.length === 0) {
        showBasicAlert("No leads found to process");
        return;
      }

      setIsLoading(true);
      setFormsSyncing(prev => new Set(prev).add(form.form_id));
      let successfulCount = 0;
      let failedCount = 0;
      const failedReasons: string[] = [];
      let syncingMsg = "";

      const batchSize = 10;
      const batches = [];

      // Commented out to only process one lead as requested
      // for (let i = 0; i < form.leads.length; i += batchSize) {
      //   batches.push(form.leads.slice(i, i + batchSize));
      // }
      
      // Process only the first lead
      if (form.leads && form.leads.length > 0) {
        batches.push([form.leads[0]]);
      }

      for (const batch of batches) {
        const batchPromises = batch.map(async (lead: Lead) => {
          try {
            const transformedResponse: any = {};

            fieldMappings.forEach((mapping) => {
              if (
                mapping.is_active &&
                lead[mapping.source_field] !== undefined
              ) {
                let value = lead[mapping.source_field];

                if (mapping.transformation_rule) {
                  if (mapping.transformation_rule === "trim") {
                    value = String(value).trim();
                  }
                  if (mapping.transformation_rule === "uppercase") {
                    value = String(value).toUpperCase();
                  }
                  if (mapping.transformation_rule === "lowercase") {
                    value = String(value).toLowerCase();
                  }
                }

                transformedResponse[mapping.target_field_label] = value;
              }
            });

            if (
              !transformedResponse["Mobile No"] &&
              !transformedResponse["mobile_no"]
            ) {
              throw new Error("Mobile number is required but not mapped");
            }

            const sourceDetails = {
              source_type: "FACEBOOK",
              facebook_page_id:
                lead.page_id ||
                form.page_id ||
                selectedPage?.id ||
                manualFetchData.page_id,
              facebook_page_name:
                lead.page_name || form.page_name || selectedPage?.name || "",
              facebook_form_id: form.form_id,
              facebook_form_name: lead.form_name || form.form_name || form.name,
              facebook_lead_id: lead.id || lead.lead_id,
              campaign_id: lead.campaign_id || null,
              campaign_name: lead.campaign_name || null,
              adset_id: lead.adset_id || null,
              adset_name: lead.adset_name || null,
              ad_id: lead.ad_id || null,
              ad_name: lead.ad_name || null,
              is_organic: lead.is_organic || false,
              fetched_at: new Date().toISOString(),
            };

            const leadData = {
              form_id: crm_form_id,
              response: transformedResponse,
              company_id: companyDetails.companyId,
              company_type: companyDetails.companyType,
              lead_source: "Social Media",
              lead_source_child: "Facebook",
              priority: "medium",
              signature: signature,
              source_details: sourceDetails,
            };

            const response = await api.post(
              "leadService/addFacebookLeadResponse",
              leadData
            );

            if (response.data.message?.[0]?.status) {
              successfulCount++;

              const resSyncLeadStatus = await api.post(
                "leadService/updateSyncingLeadStatus",
                {
                  signature: signature,
                  facebook_form_id: form.form_id,
                  syncing_lead: true,
                }
              );
              syncingMsg = resSyncLeadStatus.data?.message?.[0]?.msg;
            } else {
              throw new Error(
                response.data.message?.[0]?.msg || "Unknown error"
              );
            }
          } catch (error: any) {
            failedCount++;

            let backendMsg =
              error.response?.data?.message?.[0]?.message ||
              error.response?.data?.message?.[0]?.msg ||
              error.message ||
              "Unknown error";

            failedReasons.push(`Lead ${lead.lead_id}: ${backendMsg}`);

            console.error(`Failed to process lead ${lead.lead_id}:`, error);
          }
        });

        await Promise.all(batchPromises);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      if (successfulCount > 0) {
        setFormsSyncing(prev => {
          const newSet = new Set(prev);
          newSet.delete(form.form_id);
          return newSet;
        });
        setFormsSynced(prev => new Set(prev).add(form.form_id));
      }

    } catch (error: any) {
      console.error("Error processing Facebook leads:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg || "Error processing leads";
      showBasicAlert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsyncLeads = async (fb_form_id: any) => {
    try {
      const resSyncLeadStatus = await api.post(
        "leadService/updateSyncingLeadStatus",
        {
          signature: signature,
          facebook_form_id: fb_form_id,
          syncing_lead: false,
        }
      );

      if (resSyncLeadStatus.data?.message?.[0]?.status) {
        showBasicAlert(
          resSyncLeadStatus.data.message[0].msg ||
          "Leads unsynced successfully!"
        );

        setFormsSynced(prev => {
          const newSet = new Set(prev);
          newSet.delete(fb_form_id);
          return newSet;
        });

        setRefreshStatus((prev) => !prev);
      } else {
        showBasicAlert("Failed to unsync leads");
      }
    } catch (error: any) {
      showBasicAlert("Error unsyncing leads");
    }
  };

  const handleSyncToLeadBank = async (form: FacebookForm) => {
    setIsSyncing(form.form_id);
    try {
      const response = await syncToLeadBankAPI(
        form.form_id,
        form.name,
        selectedPageForSync?.page_id || form?.page_id || "",
        selectedPageForSync?.page_name || form?.page_name || "",
        (selectedCredential as any)?.app_scoped_user_id || ""
      );

      const resData = response?.message?.[0];

      if (resData) {
        showBasicAlert(resData.msg || "Successfully configured Lead Bank sync");

        setFormsSynced((prev) => new Set(prev).add(form.form_id));

        setFormsDestination((prev) =>
          new Map(prev).set(form.form_id, "LEAD_BANK")
        );

        setFormsSyncing((prev) => {
          const newSet = new Set(prev);
          newSet.delete(form.form_id);
          return newSet;
        });

        setRefreshStatus((prev) => !prev);
      } else {
        showBasicAlert("Failed to configure Lead Bank sync");
      }
    } catch (error) {
      showBasicAlert("Error setting up Lead Bank sync");
    } finally {
      setIsSyncing(null);
    }
  };

  const handleSyncLeads = async (form: FacebookForm) => {
    setIsSyncing(form.form_id);
    try {
      const formsFetched = await fetchCRMForms();
      if (!formsFetched) return;

      if (!selectedCrmForm) {
        showBasicAlert("Please select a CRM form first");
        setIsSyncing(null);
        return;
      }

      const mappingsResponse = await getFieldMappingsByFormAPI(
        form.form_id,
        selectedCrmForm.key
      );

      if (
        !mappingsResponse.message?.[0]?.status ||
        mappingsResponse.message[0].data.length === 0
      ) {
        showBasicAlert(
          "Please create field mappings first before processing leads"
        );
        setIsSyncing(null);
        return;
      }

      const fieldMappings = mappingsResponse.message[0].data;
      const syncStatusResponse = await getFieldMappingsSyncStatusAPI(
        form.form_id
      );

      let isAlreadySynced = false;
      if (
        syncStatusResponse.message?.[0]?.status &&
        syncStatusResponse.message[0].data
      ) {
        const mappingsWithSyncStatus = syncStatusResponse.message[0].data;
        isAlreadySynced = mappingsWithSyncStatus.some(
          (mapping: any) => mapping.syncing_lead === true
        );
      }

      if (isAlreadySynced) {
        setFormsSynced(prev => new Set(prev).add(form.form_id));
        setFormsSyncing(prev => {
          const newSet = new Set(prev);
          newSet.delete(form.form_id);
          return newSet;
        });
        setIsSyncing(null);
        showBasicAlert("Leads are already being synced to CRM");
        return;
      }

      setSyncConfirmation({
        form,
        fieldMappings,
        isAlreadySynced,
      });

      await processFacebookLeadsEnhanced(
        form,
        selectedCrmForm.key,
        fieldMappings
      );

      setFormsSynced(prev => new Set(prev).add(form.form_id));
      setFormsSyncing(prev => {
        const newSet = new Set(prev);
        newSet.delete(form.form_id);
        return newSet;
      });

      showBasicAlert("Leads synced successfully!");

    } catch (error: any) {
      console.error("Error syncing leads:", error);
      const errorMessage =
        error.response?.data?.message?.[0]?.msg || "Error syncing leads";
      showBasicAlert(errorMessage);

      setFormsSyncing(prev => {
        const newSet = new Set(prev);
        newSet.delete(form.form_id);
        return newSet;
      });
    } finally {
      setIsSyncing(null);
    }
  };

  const toProperCase = (text: any, fallback = "N/A") => {
    try {
      if (text === null || text === undefined) return fallback;

      const stringValue = typeof text === "string" ? text : String(text).trim();
      if (!stringValue) return fallback;

      const cleanText =
        stringValue.length > 100 ? stringValue.substring(0, 100) : stringValue;

      return cleanText
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();
    } catch (err) {
      console.error("Error in toProperCase:", err);
      return fallback;
    }
  };

  const generateColumns = (form: any, defaultValue = []) => {
    try {
      if (!form || typeof form !== "object" || Array.isArray(form)) {
        return defaultValue;
      }

      const keys = Object.keys(form);
      if (keys.length === 0) return defaultValue;

      const excludedKeys = [
        "ad_id",
        "ad_name",
        "adset_id",
        "adset_name",
        "campaign_id",
        "campaign_name",
        "form_id",
        "is_organic",
        "form_name",
        "page_id",
        "page_name",
      ];

      const columns = keys
        .map((key) => {
          if (typeof key !== "string" || key.trim() === "") return null;

          if (excludedKeys.includes(key.toLowerCase())) {
            return null;
          }
          const cleanKey = key.trim();
          const words = cleanKey.split(/[ _-]+/).filter(Boolean);
          const title = words.map((word) => toProperCase(word)).join(" ");
          return {
            title: title || toProperCase(cleanKey),
            dataIndex: cleanKey,
            key: cleanKey,
          };
        })
        .filter(Boolean);
      const createdTimeIndex = columns.findIndex(
        (col) => col?.dataIndex.toLowerCase() === "created_time"
      );

      if (createdTimeIndex !== -1) {
        const [createdTimeColumn] = columns.splice(createdTimeIndex, 1);
        columns.push(createdTimeColumn);
      }
      return columns;
    } catch (err) {
      console.error("Error in generateColumns:", err);
      return defaultValue;
    }
  };

  const dynamicColumns = generateColumns(selectedForm?.leads[0] || {});

  const formatValue = (value: any) => {
    if (!value) return "N/A";

    if (typeof value === "string" && value.includes("@")) {
      return <a href={`mailto:${value}`}>{value}</a>;
    }

    if (typeof value === "string" && !isNaN(Date.parse(value))) {
      return new Date(value).toLocaleString();
    }

    return value.toString();
  };

  // Credentials table columns
  const credentialsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: FacebookCredential) => (
        <div>
          <strong>{text}</strong>
          {selectedCredential?.id === record.id && (
            <span className="badge bg-primary ms-2">Editing</span>
          )}
        </div>
      ),
      sorter: (a: any, b: any) => a.name?.localeCompare(b.name),
    },
    {
      title: 'App-Scoped User ID',
      dataIndex: 'app_scoped_user_id',
      key: 'app_scoped_user_id',
      width: 250,
      render: (text: string) => <code>{text}</code>,
      sorter: (a: any, b: any) => a.app_scoped_user_id?.localeCompare(b.app_scoped_user_id),
    },
    {
      title: 'Created',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (text: string) => (
        <span>
          {text ? new Date(text).toLocaleDateString() : 'N/A'}
        </span>
      ),
      sorter: (a: any, b: any) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 300,
      render: (text: string, record: FacebookCredential) => (
        <div className="btn-group btn-group-sm">
          <button
            className="btn btn-outline-primary"
            onClick={() => loadCredential(record)}
          >
            Edit
          </button>
          <button
            className="btn btn-outline-success"
            onClick={() => handleUseCredentialAndFetchPages(record)}
            disabled={isLoading}
          >
            {isLoading ? 'Fetching...' : 'Use'}
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() =>
              record.id &&
              setDeleteRecord({
                type: 'credential',
                id: record.id,
              })
            }
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Pages table columns
  const pagesColumns = [
    {
      title: 'Page Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: any, b: any) => a.name?.localeCompare(b.name),
    },
    {
      title: 'Page ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: any, b: any) => a.id?.localeCompare(b.id),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a: any, b: any) => a.category?.localeCompare(b.category),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: string, record: FacebookPage) => (
        <button
          className="btn btn-sm btn-success"
          onClick={() => syncLeadForms(record)}
          disabled={syncingPages.has(record.id)}
        >
          {syncingPages.has(record.id) ? 'Fetching...' : 'Fetch Lead Form'}
        </button>
      ),
    },
  ];

  // Forms table columns
  const formsColumns = [
    {
      title: 'Form Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      sorter: (a: any, b: any) => a.name?.localeCompare(b.name),
    },
    {
      title: 'Form ID',
      dataIndex: 'form_id',
      key: 'form_id',
      width: 200,
      sorter: (a: any, b: any) => a.form_id?.localeCompare(b.form_id),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (text: string) => (
        <span className={`badge ${text === 'ACTIVE' ? 'bg-success' : 'bg-warning'}`}>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a.status?.localeCompare(b.status),
    },
    {
      title: 'Fetched Leads',
      dataIndex: 'leads',
      key: 'leads',
      width: 150,
      render: (leads: any[]) => (
        <span className="fw-bold text-primary">{leads?.length || 0}</span>
      ),
      sorter: (a: any, b: any) => (a.leads?.length || 0) - (b.leads?.length || 0),
    },
    {
      title: 'Created Time',
      dataIndex: 'created_time',
      key: 'created_time',
      width: 150,
      render: (text: string) => new Date(text).toLocaleDateString(),
      sorter: (a: any, b: any) => {
        const dateA = new Date(a.created_time).getTime();
        const dateB = new Date(b.created_time).getTime();
        return dateA - dateB;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 450,
      render: (text: string, record: FacebookForm) => (
        <div className="d-flex gap-2">

          {formsSynced.has(record.form_id) ? (
            <div className="d-flex align-items-center gap-2">
              <span
                className={`badge ${formsDestination.get(record.form_id) === 'LEAD_BANK' ? 'bg-primary' : 'bg-success'}`}
                title={formsDestination.get(record.form_id) === 'LEAD_BANK' ? "Leads are syncing to Lead Bank" : "Leads have already been synced to CRM"}
              >
                {formsDestination.get(record.form_id) === 'LEAD_BANK' ? 'Synced (Bank)' : 'Synced (Queue)'}
              </span>
              <button
                className="btn btn-sm btn-outline-danger"
                title="Disable CRM sync for these leads"
                onClick={() => handleUnsyncLeads(record.form_id)}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Unsync'}
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={async () => {
                  await fetchCRMForms();
                  await openMappingModal(record);
                  setShowReactFlowModal(true);
                }}
              >
                Map Fields
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={() => handleSyncLeads(record)}
                disabled={
                  !formsWithMappings.has(record.form_id) ||
                  formsSyncing.has(record.form_id) ||
                  !record.leads ||
                  record.leads.length === 0 ||
                  isSyncing === record.form_id
                }
                title={
                  !formsWithMappings.has(record.form_id)
                    ? 'Please create field mappings first'
                    : formsSyncing.has(record.form_id)
                      ? 'Sync in progress...'
                      : !record.leads || record.leads.length === 0
                        ? 'No leads available to sync'
                        : 'Sync leads to CRM Queue'
                }
              >
                {formsSyncing.has(record.form_id) || isSyncing === record.form_id ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Syncing...
                  </>
                ) : (
                  'Sync (Queue)'
                )}
              </button>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleSyncToLeadBank(record)}
                disabled={
                  formsSyncing.has(record.form_id) ||
                  isSyncing === record.form_id ||
                  !record.leads ||
                  record.leads.length === 0
                }
                title="Sync leads directly to the Lead Bank without CRM mapping"
              >
                {isSyncing === record.form_id && !formsSyncing.has(record.form_id) ? '... ' : 'Sync (Bank)'}
              </button>
            </div>
          )}

          <button
            className="btn btn-sm btn-info"
            onClick={() => openLeadsModal(record)}
            disabled={!record.leads || record.leads.length === 0}
          >
            View Leads ({record.leads?.length || 0})
          </button>
        </div>
      ),
    },
  ];

  // Field mappings table columns for the modal
  const fieldMappingsColumns = [
    {
      title: 'Facebook Field',
      dataIndex: 'source_field',
      key: 'source_field',
      width: 200,
      render: (text: string) => <code>{text}</code>,
    },
    {
      title: '→',
      key: 'arrow',
      width: 50,
      render: () => <span className="text-center">→</span>,
    },
    {
      title: 'CRM Field',
      dataIndex: 'target_field_label',
      key: 'target_field_label',
      width: 250,
      render: (text: string, record: FieldMapping) => (
        <div>
          <strong>{text}</strong>
          <br />
          <small className="text-muted">ID: {record.target_field_id}</small>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'mapping_type',
      key: 'mapping_type',
      width: 150,
      render: (text: 'automatic' | 'manual' | 'custom') => (
        <span
          className={`badge ${text === 'automatic'
            ? 'bg-success'
            : text === 'manual'
              ? 'bg-primary'
              : 'bg-warning'
            }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (text: string, record: FieldMapping) => (
        <div className="btn-group btn-group-sm">
          {!record.id && (
            <button
              className="btn btn-outline-danger"
              onClick={() => {
                setFieldMappings((prev) =>
                  prev.filter(
                    (m) =>
                      !(
                        m.source_field === record.source_field &&
                        m.target_field_id === record.target_field_id
                      )
                  )
                );
              }}
              title="Remove from current session"
            >
              Remove
            </button>
          )}

          {record.id && (
            <>
              <button
                className="btn btn-outline-primary"
                onClick={() => {
                  const newType =
                    record.mapping_type === 'manual' ? 'automatic' : 'manual';
                  updateFieldMapping(record.id!, {
                    mapping_type: newType,
                  });
                }}
                disabled={
                  fieldMappings.length === 0 || mappingState.hasMappings
                }
                title={mappingState.hasMappings ? 'Mappings already exist' : ''}
              >
                🔄
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={() =>
                  record.id &&
                  setDeleteRecord({
                    type: 'mapping',
                    id: record.id,
                  })
                }
                disabled={
                  fieldMappings.length === 0 || mappingState.hasMappings
                }
                title={mappingState.hasMappings ? 'Mappings already exist' : ''}
              >
                🗑️
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {isLoading && <Loader />}

        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">📱 Facebook Leads</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`${routes.leadsGrid}${location?.search}`}>
                    Leads
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Facebook Leads
                </li>
              </ol>
            </nav>
          </div>

          {/* Company Selector */}
          <div className="dropdown me-2 mb-2">
            <CommonSelect
              className="select"
              options={availableCompanies.map((company) => ({
                value: `${company.id}-${company.type}`,
                label: `${company.name} - ${company.type}`,
              }))}
              defaultValue={
                selectedCompany
                  ? {
                    value: `${selectedCompany.id}-${selectedCompany.type}`,
                    label: `${selectedCompany.name} - ${selectedCompany.type}`,
                  }
                  : { value: "", label: "Select Company" }
              }
              onChange={(selectedOption: any) => {
                if (selectedOption && selectedOption.value) {
                  const [companyId, companyType] =
                    selectedOption?.value?.split("-");
                  const company = availableCompanies.find(
                    (c) => c.id == parseInt(companyId) && c.type == companyType
                  );
                  if (company) handleCompanySelect(company);
                }
              }}
              placeHolder={"Select Company"}
            />
          </div>
        </div>

        {/* Show warning if no company selected */}
        {!companyDetails && (
          <div className="alert alert-warning">
            Please select a company to configure Facebook Leads integration.
          </div>
        )}

        {showDatePicker && selectedPageForSync && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Select Date Range for {selectedPageForSync.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowDatePicker(false);
                      setSelectedPageForSync(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Date Range (Optional)</label>
                    <RangePicker
                      onChange={handleDateRangeChange}
                      value={dateRange}
                      format="YYYY-MM-DD"
                      style={{ width: '100%' }}
                      placeholder={['Start Date', 'End Date']}
                    />
                    <small className="text-muted mt-2 d-block">
                      Leave empty to fetch all leads
                    </small>
                  </div>
                  {dateRange[0] && dateRange[1] && (
                    <div className="alert alert-info py-2 mb-0">
                      <small>
                        <i className="ti ti-calendar me-2"></i>
                        Selected: {dateRange[0].format('MMM DD, YYYY')} to {dateRange[1].format('MMM DD, YYYY')}
                      </small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDatePicker(false);
                      setSelectedPageForSync(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleFetchWithDate}
                    disabled={syncingPages.has(selectedPageForSync.id)}
                  >
                    {syncingPages.has(selectedPageForSync.id) ? 'Fetching...' : 'Fetch Lead Form'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="card">
          <div className="card-header">
            <ul className="nav nav-tabs nav-tabs-bottom">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "credentials" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("credentials")}
                >
                  Accounts Credentials
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pages" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("pages")}
                  disabled={!companyDetails}
                >
                  Facebook Page
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "forms" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("forms")}
                  disabled={!companyDetails}
                >
                  Facebook Lead Form
                </button>
              </li>
            </ul>
          </div>

          <div className="card-body">
            {/* Credentials Tab */}
            {activeTab === "credentials" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    {selectedCredential
                      ? "Edit Facebook Credential"
                      : "New Facebook Credential"}
                  </h5>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={createNewCredential}
                  >
                    + New Credential
                  </button>
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter name"
                        value={newCredential.name}
                        onChange={(e) =>
                          setNewCredential({
                            ...newCredential,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>User Access Token</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter access token"
                        value={newCredential.user_access_token}
                        onChange={(e) =>
                          setNewCredential({
                            ...newCredential,
                            user_access_token: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>App-Scoped User ID</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter user ID"
                        value={newCredential.app_scoped_user_id}
                        onChange={(e) =>
                          setNewCredential({
                            ...newCredential,
                            app_scoped_user_id: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={saveCredential}
                    disabled={
                      isSavingCredential ||
                      !newCredential.name ||
                      !newCredential.user_access_token ||
                      !newCredential.app_scoped_user_id
                    }
                  >
                    {isSavingCredential
                      ? "Saving..."
                      : selectedCredential
                        ? "Update Credential"
                        : "Save Credential"}
                  </button>

                  {selectedCredential && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={createNewCredential}
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {/* Saved Credentials List */}
                {savedCredentials.length > 0 && (
                  <div className="mt-4">
                    <h6>Saved Credentials</h6>
                    <div className="table-responsive">
                      <Datatable
                        columns={credentialsColumns}
                        dataSource={savedCredentials}
                        Selection={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pages Tab - Only show if company selected */}
            {companyDetails && activeTab === 'pages' && (
              <div>
                <h5 className="mb-3">Facebook Pages</h5>
                <div className="table-responsive">
                  <Datatable
                    columns={pagesColumns}
                    dataSource={userPages}
                    Selection={false}
                  />
                </div>
              </div>
            )}
            {/* Forms Tab - Only show if company selected */}
            {companyDetails && activeTab === 'forms' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Lead Form</h5>
                  {selectedPage && (
                    <div>
                      <span className="text-muted me-3">
                        Page Name: <strong>{selectedPage.name}</strong>
                      </span>
                      <span className="text-muted">
                        Page Id: <strong>{selectedPage.id}</strong>
                      </span>
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Page Access Token</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedPage?.access_token || ''}
                    readOnly
                  />
                </div>

                <div className="table-responsive">
                  <Datatable
                    columns={formsColumns}
                    dataSource={pageForms}
                    Selection={false}
                  />
                </div>
              </div>
            )}
            {/* Show message if company not selected for other tabs */}
            {!companyDetails && activeTab !== "credentials" && (
              <div className="text-center py-4">
                <p className="text-muted">
                  Please select a company first to access this tab.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* React Flow Mapping Modal */}
        {showReactFlowModal && selectedFormForMapping && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xxl" style={{ maxWidth: "95%" }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    📊 Field Mapping - {selectedFormForMapping.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeMappingModal}
                  ></button>
                </div>
                <div className="modal-body">
                  {/* Header with clear labels */}
                  <div className="row mb-4">
                    <div className="col-md-5">
                      <div className="card bg-light">
                        <div className="card-body text-center">
                          <h6 className="mb-1">📱 Facebook Lead Fields</h6>
                          <small className="text-muted">
                            Source: Data from Facebook forms
                          </small>
                          <div className="mt-2">
                            <span className="badge bg-info">
                              {getAvailableFacebookFields().length} fields
                              available
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2 text-center align-self-center">
                      <div className="arrow-container">
                        <span className="fs-2">→</span>
                        <br />
                        <small className="text-muted">Map To</small>
                      </div>
                    </div>
                    <div className="col-md-5">
                      <div className="card bg-light">
                        <div className="card-body text-center">
                          <h6 className="mb-1">🏢 CRM Form Fields</h6>
                          <small className="text-muted">
                            Destination: Your CRM system
                          </small>
                          <div className="mt-2">
                            <span className="badge bg-info">
                              {selectedCrmForm?.structure?.length || 0} fields
                              available
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CRM Form Selection */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <label className="fw-bold">Select CRM Form</label>
                          <select
                            className="form-control"
                            value={mappingState.crmFormId || selectedCrmForm?.key || ""}
                            onChange={(e) => {
                              if (!mappingState.hasMappings) {
                                const form = crmForms.find(
                                  (f) => f.key === parseInt(e.target.value)
                                );
                                setSelectedCrmForm(form || null);
                              }
                            }}
                            disabled={mappingState.hasMappings}
                          >
                            <option value="">Select a CRM Form</option>
                            {crmForms.map((form) => (
                              <option key={form.key} value={form.key}>
                                {form.LeadFormName} ({form.Pipeline})
                                {mappingState.crmFormId === form.key ? " (Currently Mapped)" : ""}
                              </option>
                            ))}
                          </select>


                          {mappingState.hasMappings && (
                            <div className="mt-2">
                              <div className="alert alert-warning alert-dismissible fade show py-2" role="alert">
                                <i className="ti ti-info-circle me-2"></i>
                                <small>
                                  CRM Form selection is disabled because mappings already exist.
                                  To change the CRM form, you need to delete all existing mappings first.
                                </small>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex align-items-end h-100 gap-2">
                            <button
                              className="btn btn-success"
                              onClick={autoMapToCRMFields}
                            >
                              🚀 Auto-Map
                            </button>
                            <button
                              className="btn btn-info"
                              onClick={() =>
                                showBasicAlert(
                                  `📊 Mapping Status:\n\n` +
                                  `• Facebook Fields: ${getAvailableFacebookFields().length
                                  }\n` +
                                  `• CRM Fields: ${selectedCrmForm?.structure?.length || 0
                                  }\n` +
                                  `• Mapped: ${fieldMappings.length}\n` +
                                  `• Coverage: ${Math.round(
                                    (fieldMappings.length /
                                      getAvailableFacebookFields().length) *
                                    100
                                  )}%`
                                )
                              }
                            >
                              📊 Stats
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* React Flow Container */}
                  {selectedCrmForm && (
                    <ReactFlowProvider>
                      <FieldMappingFlow
                        facebookFields={getAvailableFacebookFields()}
                        crmFormFields={selectedCrmForm.structure}
                        existingMappings={fieldMappings}
                        onMappingsChange={setFieldMappings}
                        onClose={closeMappingModal}
                        selectedFormForMapping={selectedFormForMapping}
                        selectedCrmForm={selectedCrmForm}
                      />
                    </ReactFlowProvider>
                  )}
                </div>

                {/* Current Mappings Summary */}
                {fieldMappings.length > 0 && (
                  <div className="card mt-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        📋 Current Mappings ({fieldMappings.length})
                      </h6>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          if (!selectedFormForMapping || !selectedCrmForm) {
                            showBasicAlert(
                              'Please select both Facebook form and CRM form'
                            );
                            return;
                          }
                          setDeleteRecord({
                            type: 'allMappings',
                            data: {
                              facebookFormId: selectedFormForMapping.form_id,
                              crmFormId: selectedCrmForm.key,
                              formName: selectedFormForMapping.name,
                            },
                          });
                        }}
                      >
                        🗑️ Delete All
                      </button>
                    </div>
                    <div className="card-body">
                      <Datatable
                        columns={fieldMappingsColumns}
                        dataSource={fieldMappings}
                        Selection={false}
                      />
                    </div>
                  </div>
                )}
                <div className="modal-footer">
                  <div className="me-auto">
                    <span className="text-muted">
                      Mapped: <strong>{fieldMappings.length}</strong> of{" "}
                      <strong>{getAvailableFacebookFields().length}</strong>{" "}
                      fields
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeMappingModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveAllMappings}
                    disabled={fieldMappings.length === 0 || mappingState.hasMappings}
                    title={mappingState.hasMappings ? "Mappings already exist. To save new mappings, delete existing ones first." : ""}
                  >
                    {mappingState.hasMappings ? (
                      <>
                        <i className="ti ti-lock me-2"></i>
                        Mappings Already Exist
                      </>
                    ) : (
                      <>
                        💾 Save {fieldMappings.length} Mapping
                        {fieldMappings.length !== 1 ? "s" : ""}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Leads Modal */}
        {showLeadsModal && selectedForm && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-xl" style={{ maxWidth: '95%' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Leads for: {selectedForm.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeLeadsModal}
                  ></button>
                </div>
                <div
                  className="modal-body"
                  style={{ maxHeight: '70vh', overflowY: 'auto' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <span className="badge bg-primary me-2">
                        Total Leads: {selectedForm.total_leads}
                      </span>
                      <span className="badge bg-success me-2">
                        Fetched: {selectedForm.leads?.length || 0}
                      </span>
                      <span
                        className={`badge ${selectedForm.status === 'ACTIVE'
                          ? 'bg-success'
                          : 'bg-warning'
                          }`}
                      >
                        {selectedForm.status}
                      </span>
                    </div>
                    <small className="text-muted">
                      Form ID: {selectedForm.form_id}
                    </small>
                  </div>

                  {selectedForm.leads && selectedForm.leads.length > 0 ? (
                    <Datatable
                      columns={dynamicColumns}
                      dataSource={selectedForm.leads}
                      Selection={false}
                    />
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-muted">
                        <i className="ti ti-inbox fs-1"></i>
                        <p className="mt-2">No leads found for this form</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeLeadsModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <CustomDeleteModal />
      </div>
    </div>
  );
};

export default FacebookLeadsConfig;
